import type { PrefetchVariant } from '~/core/entities/property';

import {
	getSavedBandwidthEstimate,
	primeManifests,
	saveBandwidthEstimateFromProbe
} from '~/composables/useHlsPlayer/shakaLoader';
import {
	getProxiedVideoUrl,
	prefetchFirstSegment,
	resolveFirstSegmentUrl,
	resolveHlsWarmupUrls
} from '~/composables/useVideoUrls';
import {
	claimWarmSection,
	releaseWarmSection,
	trySpendWarmBudget,
	WARM_COST_SEGMENT
} from '~/composables/useWarmScheduler';
import { waitForFirstSignal } from '~/utils/waitForFirstSignal';

import {
	isIOSDevice,
	isIOSNativePlayback
} from '~/components/PropertyReelsFeed/useIOSSharedVideo';
import {
	REEL_THUMB_FORMAT,
	REEL_THUMB_HEIGHT,
	REEL_THUMB_QUALITY,
	REEL_THUMB_SIZES,
	REEL_THUMB_WIDTH
} from '~/components/VideoCard/reelThumbnailPreset';

/**
 * Warm-up helpers for HLS video delivery: SSR head hints (preconnect/preload)
 * and client-side manifest warming. Extracted from the shorts page and
 * ShortsSection so every call site shares the same guards (malformed URLs,
 * Safari's missing requestIdleCallback, Data Saver).
 */

/** Origin of a video URL, or null when the URL is absent/relative/malformed. */
function safeVideoOrigin(url: string | null | undefined): string | null {
	if (!url?.startsWith('http')) return null;
	try {
		return new URL(url).origin;
	} catch {
		return null;
	}
}

/**
 * Emits a `preconnect` head link for the video CDN, derived from an actual
 * video URL so a CDN domain change never leaves a stale hardcode. No-op when
 * the URL is absent or malformed. Must be called during component setup.
 */
export function useVideoCdnPreconnect(
	videoUrl: string | null | undefined
): void {
	const origin = safeVideoOrigin(videoUrl);
	if (!origin) return;
	useHead({
		link: [
			{
				rel: 'preconnect',
				href: origin,
				crossorigin: 'anonymous'
			}
		]
	});
}

/**
 * SSR head hints for the entry video of a shorts page: preconnect + preload of
 * the full HLS chain (master → lowest variant playlist → first segment), so
 * the video downloads in parallel with the JS bundle instead of after
 * hydration.
 *
 * - Server-only resolution (`resolveHlsWarmupUrls`: memory + Redis cache,
 *   800ms timeout). On failure the page renders without hints — the player's
 *   own fetch flow is untouched.
 * - The master preload URL must match the player's request exactly
 *   (getProxiedVideoUrl appends `_cb=1`); variant/segment URLs carry only
 *   `v=HASH`, matching Shaka's scheme handler.
 * - The video chain (playlist/init/first segment) is fetchpriority=high — it
 *   is what gates the first frame; the audio first segment stays at the
 *   default priority.
 *
 * Must be awaited during component setup (uses useAsyncData/useHead).
 */
export async function useShortsHlsWarmup(
	rawMasterUrl: string | null | undefined,
	asyncDataKey: string
): Promise<void> {
	const proxiedMasterUrl = getProxiedVideoUrl(rawMasterUrl);
	const cdnOrigin = safeVideoOrigin(proxiedMasterUrl);
	if (!proxiedMasterUrl || !cdnOrigin) return;

	useHead({
		link: [
			{
				rel: 'preconnect',
				href: cdnOrigin,
				crossorigin: 'anonymous'
			},
			{
				rel: 'preload',
				as: 'fetch',
				href: proxiedMasterUrl,
				crossorigin: 'anonymous'
			}
		]
	});

	const { data: warmup } = await useAsyncData(
		`shorts-hls-warmup:${asyncDataKey}`,
		async () => {
			if (!import.meta.server || !rawMasterUrl?.startsWith('http')) {
				return null;
			}
			return resolveHlsWarmupUrls(rawMasterUrl, AbortSignal.timeout(800));
		},
		{ default: () => null }
	);

	if (!warmup.value) return;

	// Os textos vieram no payload: o player parseia manifests sem rede.
	// As chaves do resolver usam o host CRU; o player pede pelo host proxiado
	// — prima nas duas formas para o lookup nunca errar.
	if (import.meta.client && warmup.value.manifests && rawMasterUrl) {
		const primed: Record<string, string> = { ...warmup.value.manifests };
		try {
			const rawOrigin = new URL(rawMasterUrl).origin;
			const proxiedOrigin = new URL(proxiedMasterUrl).origin;
			if (rawOrigin !== proxiedOrigin) {
				for (const [url, text] of Object.entries(warmup.value.manifests)) {
					primed[url.replace(rawOrigin, proxiedOrigin)] = text;
				}
			}
			primed[proxiedMasterUrl] =
				warmup.value.manifests[rawMasterUrl] ??
				Object.values(warmup.value.manifests)[0]!;
		} catch {
			// URLs malformadas: segue só com as chaves originais
		}
		primeManifests(primed);
	}

	// The segment preload is UNIVERSAL — no per-request gate. Two reasons:
	//
	// 1. This HTML is cached by CloudFront with `Vary: Accept-Encoding` alone,
	//    so any per-request decision here (the previous Save-Data check, or a
	//    network client hint) leaks: whoever populates the cache entry fixes
	//    the preload set for every visitor until the TTL expires. A gate that
	//    fires at random is worse than no gate.
	// 2. This is the entry video of its own page — playback is near-certain,
	//    so the segment bytes are needed either way. Starting them at HTML
	//    parse beats starting after hydration or after the tap, especially on
	//    slow networks. On this page the first frame IS the product, so the
	//    chain is not demoted below the bundle: the whole video chain
	//    (playlist/init/first segment) goes out high; audio segments stay at
	//    default priority since they don't gate the frame.
	//    The cost is borne only by visitors who bounce without playing.
	//
	// Speculative contexts (carousels) DO gate — see useCarouselSegmentWarmup.
	useHead({
		link: [
			...(warmup.value.variantPlaylistUrl
				? [
						{
							rel: 'preload',
							as: 'fetch' as const,
							href: warmup.value.variantPlaylistUrl,
							crossorigin: 'anonymous' as const,
							fetchpriority: 'high' as const
						}
					]
				: []),
			...(warmup.value.initSegmentUrl
				? [
						{
							rel: 'preload',
							as: 'fetch' as const,
							href: warmup.value.initSegmentUrl,
							crossorigin: 'anonymous' as const,
							fetchpriority: 'high' as const
						}
					]
				: []),
			...(warmup.value.firstSegmentUrl
				? [
						{
							rel: 'preload',
							as: 'fetch' as const,
							href: warmup.value.firstSegmentUrl,
							crossorigin: 'anonymous' as const,
							// E este segmento que destrava o primeiro frame; o de
							// audio nao, por isso so a cadeia de video sobe aqui.
							fetchpriority: 'high' as const
						}
					]
				: []),
			// Separate-audio streams (CMAF): without these the play() moment
			// discovers the audio chain serially and stalls ~1-2s on it.
			...[
				warmup.value.audioPlaylistUrl,
				warmup.value.audioInitSegmentUrl,
				warmup.value.audioFirstSegmentUrl
			]
				.filter((href): href is string => Boolean(href))
				.map(href => ({
					rel: 'preload',
					as: 'fetch' as const,
					href,
					crossorigin: 'anonymous' as const,
					...(href !== warmup.value?.audioFirstSegmentUrl
						? { fetchpriority: 'high' as const }
						: {})
				}))
		]
	});
}

// One-shot session warm of the contact-form modal chunk (phone country data
// and validation, ~127KB): after the entry video's first frame, on idle, so
// the first "Tenho interesse" tap opens instantly without the chunk ever
// competing with video segments. Loader injected to keep the import graph
// (and its chunk) inside the calling component.
let contactModalWarmRegistered = false;

// O botao de lead so fica clicavel quando o chunk da modal esta pronto.
export const contactModalChunkReady = ref(false);

// Teto do warm: o primeiro frame NAO e garantido (reel so de imagem, erro de
// video) e sem teto o botao de lead ficaria desabilitado para sempre.
const CONTACT_WARM_CAP_MS = 6000;
const CONTACT_WARM_GESTURES = ['pointerdown', 'keydown', 'wheel'] as const;

export function registerShortsContactModalWarm(
	firstFrameEventName: string,
	loadChunk: () => Promise<unknown>
): void {
	if (!import.meta.client || contactModalWarmRegistered) return;
	contactModalWarmRegistered = true;
	const load = () =>
		void loadChunk()
			.then(() => {
				contactModalChunkReady.value = true;
			})
			.catch(() => {
				// Preload falhou: libera o botao — o clique faz o proprio import.
				contactModalChunkReady.value = true;
			});
	waitForFirstSignal(
		[firstFrameEventName],
		CONTACT_WARM_CAP_MS,
		CONTACT_WARM_GESTURES
	).promise.then(() => {
		if ('requestIdleCallback' in window) {
			requestIdleCallback(load, { timeout: 3000 });
		} else {
			setTimeout(load, 2000);
		}
	});
}

// Ceiling for SPECULATIVE segment warm-ups (visible carousels): only variants
// at or below this declared bandwidth — the same ceiling the intent prefetch
// uses on slow networks (INTENT_MAX_SEGMENT_BPS_ON_SLOW). At ~2.5 Mbps a
// segment is a few hundred KB; above it, speculation costs more than it saves.
// The entry-video preload is NOT capped by this: there, playback is certain.
const SPECULATIVE_SEGMENT_MAX_BPS = 2_500_000;

// The Shaka chunk only needs to warm once per session — the browser HTTP
// cache and Vite's module cache make repeats free, but the idle callback
// bookkeeping isn't.
let playerChunkWarmed = false;

/**
 * Warms the hls.js player chunk from pages that can navigate into
 * shorts (home/SRP/PDP sections), so the first play skips the biggest serial
 * download. Import-only — no polyfills or cache init run until the player
 * actually mounts and calls loadHlsEngine().
 *
 * Deliberately zero-impact on the hosting page: client-only, once per
 * session, skipped under Data Saver and on iOS native playback (which never
 * loads the MSE player), and deferred to idle time so it never competes with
 * hydration, LCP or user input. Never throws.
 */
export function prefetchShakaChunkOnIdle(): void {
	if (!import.meta.client || playerChunkWarmed) return;

	try {
		const connection = (
			navigator as Navigator & { connection?: { saveData?: boolean } }
		).connection;
		if (connection?.saveData) return;
		if (isIOSNativePlayback()) return;

		playerChunkWarmed = true;
		const run = () => {
			import('hls.js').catch(() => {
				playerChunkWarmed = false;
			});
		};

		if ('requestIdleCallback' in window) {
			requestIdleCallback(run, { timeout: 10_000 });
		} else {
			setTimeout(run, 2000);
		}
	} catch {
		// Warm-up is best-effort — never propagate into the caller.
	}
}

// Videos already prefetched by intent this session — a click or hover repeat
// must not re-download. Capped so an infinite-scroll session can't grow it.
const intentPrefetched = new Set<string>();
const INTENT_PREFETCH_MAX_ENTRIES = 300;
// On slow networks, only pull first segments of variants at or below this
// declared bandwidth — an old single-variant original (4-9 Mbps) would hog
// the thin pipe right when the feed is about to mount and need it.
const INTENT_MAX_SEGMENT_BPS_ON_SLOW = 2_500_000;
const FAST_NETWORK_BPS = 4_000_000;

function rememberIntent(url: string): boolean {
	if (intentPrefetched.has(url)) return false;
	if (intentPrefetched.size >= INTENT_PREFETCH_MAX_ENTRIES) {
		const oldest = intentPrefetched.values().next().value;
		if (oldest !== undefined) intentPrefetched.delete(oldest);
	}
	intentPrefetched.add(url);
	return true;
}

/**
 * Whether this video's chain was already prefetched by user intent in this
 * session — lets QoE telemetry separate warmed plays from cold plays.
 * Accepts raw or already-proxied URLs: re-proxying an already-proxied URL
 * appends the cache-bust param twice and would never match the stored key.
 */
export function wasVideoIntentPrefetched(
	videoUrl: string | null | undefined
): boolean {
	if (!videoUrl) return false;
	if (intentPrefetched.has(videoUrl)) return true;
	const url = getProxiedVideoUrl(videoUrl);
	return url ? intentPrefetched.has(url) : false;
}

// Warm de cadeia (payload/scroll) concluido nesta sessao. Registro separado do
// intent: sem ele o flag `warmed` da QoE so enxergava o caminho de clique e
// subestimava o beneficio medido do preload (carrossel/PDP ficavam cegos).
const chainWarmed = new Set<string>();

export function rememberChainWarm(url: string | null | undefined): void {
	if (!url) return;
	if (chainWarmed.size >= INTENT_PREFETCH_MAX_ENTRIES) {
		const oldest = chainWarmed.values().next().value;
		if (oldest !== undefined) chainWarmed.delete(oldest);
	}
	chainWarmed.add(url);
}

/** Intent OU cadeia aquecida: o sinal que a QoE deve usar como `warmed`. */
export function wasVideoWarmed(videoUrl: string | null | undefined): boolean {
	if (!videoUrl) return false;
	if (wasVideoIntentPrefetched(videoUrl)) return true;
	if (chainWarmed.has(videoUrl)) return true;
	const url = getProxiedVideoUrl(videoUrl);
	return url ? chainWarmed.has(url) : false;
}

/**
 * Strong-intent prefetch: the user clicked something that navigates to this
 * video (feed card, cluster card) OR swiped an autoplaying carousel toward it
 * (the next active card plays by itself). Downloading its chain is not
 * speculative — the player will request these exact bytes moments from now.
 * Pulls master + variant playlist + first segment into the CacheStorage the
 * player reads from.
 *
 * On slow networks the segment is capped by declared bandwidth so a heavy
 * legacy variant doesn't hog the pipe the feed is about to need; manifests
 * always warm. Fire-and-forget — never throws, never blocks navigation.
 */
// Duas intencoes simultaneas no cano (nao uma so): comparar duas fichas
// indo e voltando entre elas — um padrao comum — e mais do que uma troca
// dentro da janela de debounce do chamador, entao as duas ficam com dwell
// real. Com uma vaga so, A->B->A abortava e reiniciava a cada troca e
// nenhum prefetch terminava. Uma terceira intencao ainda evita o mais antigo.
const MAX_CONCURRENT_INTENT_PREFETCHES = 2;
const activeIntentPrefetches = new Map<string, AbortController>();

export function prefetchVideoOnIntent(
	videoUrl: string | null | undefined,
	variants?: PrefetchVariant[] | null
): void {
	if (!import.meta.client || !videoUrl) return;

	// Warming does nothing measurable on iOS, on either pipeline, so the bytes
	// are paid twice for no gain. Measured in production on 10-11/08, startup
	// p75 warmed vs cold: 0.93s vs 1.01s on the native AVPlayer (which reads
	// its own network stack, not this cache) and 0.79s vs 0.75s on MSE once
	// the flag rolled out. Compare with desktop Shaka in the same window,
	// 0.59s vs 1.60s, to see what a warm that works looks like.
	//
	// Revisit if the iOS numbers ever show a gap: the skip is here to stop
	// paying for nothing, not because warming is wrong in principle.
	if (isIOSDevice) return;

	const url = getProxiedVideoUrl(videoUrl);
	if (!url || !rememberIntent(url)) return;

	try {
		const bps = getSavedBandwidthEstimate();
		// This prefetch only fires on the click that opens the feed on this
		// exact video — playback is certain, and on legacy single-variant
		// videos the player would download the very same heavy segment moments
		// later anyway. Skipping here would only lose the parallelism with the
		// navigation, so an unknown estimate (first visit) does not cap.
		const networkIsFast = bps === null || bps >= FAST_NETWORK_BPS;

		if (activeIntentPrefetches.size >= MAX_CONCURRENT_INTENT_PREFETCHES) {
			const oldestUrl = activeIntentPrefetches.keys().next().value;
			if (oldestUrl !== undefined) {
				activeIntentPrefetches.get(oldestUrl)?.abort();
				activeIntentPrefetches.delete(oldestUrl);
				// Abortar sem desmarcar deixava a cadeia fria pelo resto da sessao
				// e sujava a comparacao warmed-vs-cold do QoE.
				intentPrefetched.delete(oldestUrl);
			}
		}

		const intentAbort = new AbortController();
		activeIntentPrefetches.set(url, intentAbort);
		const clearIfCurrent = () => {
			if (activeIntentPrefetches.get(url) !== intentAbort) return;
			activeIntentPrefetches.delete(url);
		};
		void prefetchFirstSegment(
			url,
			intentAbort.signal,
			'lowest',
			networkIsFast ? undefined : INTENT_MAX_SEGMENT_BPS_ON_SLOW,
			variants
		)
			.catch(() => {})
			.finally(clearIfCurrent);
	} catch {
		// Best-effort — never let a prefetch failure affect the click.
	}
}

// Reel posters already warmed this session — an <img> retry is cheap, but the
// bookkeeping keeps hover storms from re-creating Image objects.
const posterIntentWarmed = new Set<string>();

/**
 * Intent warm of the reel entry poster: requests the exact IPX candidate the
 * feed's NuxtImg (REEL_THUMB preset) will render, so after the navigation the
 * poster paints from cache instead of starting its download post-mount. Uses
 * an Image with the same srcset/sizes so the browser picks the same candidate.
 */
export function prefetchReelPosterOnIntent(
	thumbnailUrl: string | null | undefined
): void {
	if (!import.meta.client || !thumbnailUrl) return;
	if (posterIntentWarmed.has(thumbnailUrl)) return;
	if (posterIntentWarmed.size >= INTENT_PREFETCH_MAX_ENTRIES) {
		const oldest = posterIntentWarmed.values().next().value;
		if (oldest !== undefined) posterIntentWarmed.delete(oldest);
	}
	posterIntentWarmed.add(thumbnailUrl);
	try {
		const sizes = useImage().getSizes(thumbnailUrl, {
			sizes: REEL_THUMB_SIZES,
			modifiers: {
				format: REEL_THUMB_FORMAT,
				quality: REEL_THUMB_QUALITY,
				width: REEL_THUMB_WIDTH,
				height: REEL_THUMB_HEIGHT
			}
		});
		const img = new Image();
		if (sizes.sizes) img.sizes = sizes.sizes;
		if (sizes.srcset) img.srcset = sizes.srcset;
		img.src = sizes.src ?? thumbnailUrl;
	} catch {
		// Best-effort — a missed poster warm costs nothing.
	}
}

/**
 * Speculative hover warm: the user is pointing at a card but has not clicked.
 * Only the master manifest (~400 B) is fetched — enough to remove a round
 * trip from hover-to-play and from a subsequent click, cheap enough to fire
 * on every card the pointer crosses.
 */
export function warmVideoManifestOnHover(
	videoUrl: string | null | undefined
): void {
	if (!import.meta.client || !videoUrl) return;
	const url = getProxiedVideoUrl(videoUrl);
	if (!url || intentPrefetched.has(url)) return;
	try {
		fetch(url, { priority: 'low' } as RequestInit).catch(() => {});
	} catch {
		// Best-effort.
	}
}

/**
 * Warms the HLS master manifests (~400 B each) of the first videos in a list
 * so that when a card autoplays on scroll or hover, the player skips the
 * manifest round-trip. Manifest-only — segments are too heavy to speculate on
 * from a carousel.
 *
 * Guards: client-only, skipped under Data Saver, deferred to idle time
 * (setTimeout fallback for Safari, which lacks requestIdleCallback), and
 * never throws — a warm failure must not break the caller's data flow.
 */
export function warmVideoManifests(
	videoUrls: Array<string | null | undefined>,
	count: number = 2
): void {
	if (!import.meta.client) return;

	try {
		const connection = (
			navigator as Navigator & { connection?: { saveData?: boolean } }
		).connection;
		if (connection?.saveData) return;

		const run = () => {
			videoUrls
				.filter((url): url is string => url != null)
				.slice(0, count)
				.forEach(rawUrl => {
					const url = getProxiedVideoUrl(rawUrl);
					if (!url || intentPrefetched.has(url)) return;
					fetch(url, { priority: 'low' } as RequestInit).catch(() => {});
				});
		};

		if ('requestIdleCallback' in window) {
			requestIdleCallback(run);
		} else {
			setTimeout(run, 200);
		}
	} catch {
		// Warm-up is best-effort — never propagate into the caller.
	}
}

// Speculative segment warm-up for visible carousels: how many cards deep to
// go, and how long any single warm may run. The user browses left-to-right,
// so the first cards are the likeliest taps.
// Only the first card: it is the only one with real click probability before
// any interaction. Later cards earn their warm through signals (hover, the
// slider's next-card warm, pointerdown).
const CAROUSEL_SEGMENT_WARM_COUNT = 1;
const CAROUSEL_SEGMENT_WARM_TIMEOUT_MS = 10_000;

/**
 * Lazily pulls the first segment of a visible carousel's first videos while
 * the user browses, so a tap starts playback from CacheStorage instead of the
 * network. Sits between the manifest warm (data arrival / hover) and the
 * confirmed-intent prefetch (click) — it downloads real video bytes, so every
 * guard is stacked:
 *
 * - hover-capable pointers only (desktop): there, hovering a card PLAYS it
 *   (VideoCard's hoverToPlay), so the warmed segment has a real consumer. On
 *   touch devices no hover exists — a tap navigates to the feed, where the
 *   click prefetch plus the entry page's own SSR preload already cover the
 *   chain, so a carousel warm would be speculative bytes with no consumer;
 * - only after the section is actually visible (caller's IntersectionObserver);
 * - only when this user's MEASURED bandwidth is fast (no estimate = no bytes —
 *   speculating on an unknown, possibly metered pipe is how the SSR segment
 *   preload went wrong);
 * - only light variants (SPECULATIVE_SEGMENT_MAX_BPS cap → a few hundred KB);
 * - one video at a time, each queued behind an idle callback, so the queue
 *   never competes with hydration, LCP or user input;
 * - deduped against every other warm layer via the shared intent set;
 * - skipped under Data Saver, on iOS native playback (which never reads the
 *   Shaka cache) and in hidden tabs; abandoned when the caller's scope dies.
 *
 * Call during component setup; re-fires when visibility or the list changes.
 */
export function useCarouselSegmentWarmup(
	videoUrls: () => Array<string | null | undefined>,
	isVisible: () => boolean,
	// Sections sharing a page arbitrate the warm right through the scheduler;
	// callers without an id keep the legacy per-section behaviour.
	sectionId?: string
): void {
	if (!import.meta.client) return;

	let disposed = false;
	onScopeDispose(() => {
		disposed = true;
		if (sectionId) releaseWarmSection(sectionId);
	});

	const idle = (run: () => void): void => {
		if ('requestIdleCallback' in window) {
			requestIdleCallback(run, { timeout: 10_000 });
		} else {
			setTimeout(run, 1000);
		}
	};

	const warmQueue = (urls: string[]): void => {
		const [next, ...rest] = urls;
		if (!next || disposed) return;
		idle(() => {
			// Visibility is re-checked at fire time: an idle callback can run
			// long after the user scrolled the section away.
			if (disposed || document.hidden || !isVisibleNow()) return;
			if (!trySpendWarmBudget(WARM_COST_SEGMENT)) return;
			if (!rememberIntent(next)) {
				warmQueue(rest);
				return;
			}
			void prefetchFirstSegment(
				next,
				AbortSignal.timeout(CAROUSEL_SEGMENT_WARM_TIMEOUT_MS),
				'lowest',
				SPECULATIVE_SEGMENT_MAX_BPS
			)
				.catch(() => {})
				.finally(() => warmQueue(rest));
		});
	};

	const isVisibleNow = (): boolean => {
		try {
			return isVisible();
		} catch {
			return false;
		}
	};

	const attempt = (): void => {
		const visible = isVisible();
		const urls = videoUrls();
		if (disposed || !visible || urls.length === 0) return;
		// One section per page speculates at a time; the claim transfers when
		// the holder scrolls away (it releases on dispose/invisibility below).
		if (sectionId && !claimWarmSection(sectionId)) return;
		try {
			// No hover, no consumer: touch devices only fetch on tap.
			if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches)
				return;
			const connection = (
				navigator as Navigator & { connection?: { saveData?: boolean } }
			).connection;
			if (connection?.saveData) return;
			if (isIOSNativePlayback()) return;
			const bps = getSavedBandwidthEstimate();
			if (bps === null) {
				probeBandwidthForCarousel(urls, attempt);
				return;
			}
			if (bps < FAST_NETWORK_BPS) return;

			const proxied = urls
				.filter((url): url is string => url != null)
				.slice(0, CAROUSEL_SEGMENT_WARM_COUNT)
				.map(url => getProxiedVideoUrl(url))
				.filter((url): url is string => url !== null)
				.filter(url => !intentPrefetched.has(url));
			warmQueue(proxied);
		} catch {
			// Warm-up is best-effort — never propagate into the caller.
		}
	};

	watch(
		[isVisible, videoUrls] as const,
		([visible]) => {
			if (!visible && sectionId) {
				// Scrolled away: hand the warm right to whichever section the
				// user is looking at now.
				releaseWarmSection(sectionId);
				return;
			}
			attempt();
		},
		{ immediate: true }
	);
}

let carouselProbeState: 'idle' | 'running' | 'done' = 'idle';

function probeBandwidthForCarousel(
	urls: Array<string | null | undefined>,
	onDone: () => void
): void {
	if (carouselProbeState !== 'idle') return;
	const firstUrl = urls.find((url): url is string => url != null);
	const proxied = firstUrl ? getProxiedVideoUrl(firstUrl) : null;
	if (!proxied) return;
	carouselProbeState = 'running';

	const run = (): void => {
		void (async () => {
			try {
				const segmentUrl = await resolveFirstSegmentUrl(
					proxied,
					AbortSignal.timeout(8_000)
				);
				if (segmentUrl) {
					const probeUrl = new URL(segmentUrl);
					probeUrl.searchParams.set('_cb', '1');
					await saveBandwidthEstimateFromProbe(
						probeUrl.toString(),
						AbortSignal.timeout(8_000)
					);
				}
			} catch {
				// noop
			} finally {
				carouselProbeState = 'done';
				onDone();
			}
		})();
	};

	if ('requestIdleCallback' in window) {
		requestIdleCallback(run, { timeout: 5_000 });
	} else {
		setTimeout(run, 1_500);
	}
}

// Teto de banda do warm de cadeia a partir do payload: acima disto o degrau e
// um original legado de 4-9 Mbps cujo primeiro segmento domina o cano.
const CHAIN_WARM_MAX_SEGMENT_BPS = 2_500_000;
const CHAIN_WARM_FAST_NETWORK_BPS = 4_000_000;

export interface ChainWarmer {
	/** Aquece master+variant+primeiro segmento; no-op se a mesma URL ja foi pedida. */
	warm: (
		url: string | null | undefined,
		ladder: PrefetchVariant[] | null | undefined
	) => void;
	/** Aborta o warm em curso e libera a marca, permitindo refazer depois. */
	abort: () => void;
}

/**
 * Um warmer por instancia de player (galeria e card de carrossel usavam a
 * mesma logica copiada duas vezes, ja com os mesmos limiares hardcoded nos
 * dois lugares) — mesma marca de URL-ja-pedida e mesmo abort-and-restart.
 */
export function createChainWarmer(): ChainWarmer {
	let chainWarmAbort: AbortController | null = null;
	let warmedChainUrl: string | null = null;

	function warm(
		url: string | null | undefined,
		ladder: PrefetchVariant[] | null | undefined
	): void {
		if (!import.meta.client) return;
		if (!url || warmedChainUrl === url) return;
		warmedChainUrl = url;
		chainWarmAbort?.abort();
		const abort = new AbortController();
		chainWarmAbort = abort;
		const bps = getSavedBandwidthEstimate();
		const networkIsFast = bps !== null && bps >= CHAIN_WARM_FAST_NETWORK_BPS;
		void prefetchFirstSegment(
			url,
			abort.signal,
			'lowest',
			networkIsFast ? undefined : CHAIN_WARM_MAX_SEGMENT_BPS,
			ladder,
			// Sem escada (acervo legado) o segmento pode ter megabytes e o bitrate
			// so aparece depois de baixar: aquece so os manifestos.
			!ladder?.length
		)
			.then(() => rememberChainWarm(url))
			.catch(() => {
				// Warm e best-effort: o player busca pela rede como sempre fez.
			});
	}

	function abort(): void {
		chainWarmAbort?.abort();
		chainWarmAbort = null;
		// Zerar a marca permite refazer um warm interrompido pela metade.
		warmedChainUrl = null;
	}

	return { warm, abort };
}
